import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { format } from 'sql-formatter';
import { AsyncLoadStatus } from '../common/types';
import { cn } from '../common/utils';
import { Button } from '../components/actions/Button';
import CopyToClipboardButton from '../components/actions/CopyToClipboardButton';
import CodeEditor from '../components/editors/CodeEditor';
import { FormErrorField } from '../components/FormError';
import { Container } from '../components/layout/ContainerFlex';
import { Spacer } from '../components/layout/Spacer';
import { ActionBar } from '../components/nav/ActionBar';
import PanelContainer from '../containers/PanelContainer';
import useLoadCustomQueryData from './hooks/useLoadCustomQueryData';
import {
  customQueryDataLoadStatus,
  customQueryDescriptorById,
  customQueryErrors,
  customQueryStateById,
} from './store/customQuery.state';

interface CustomQueryEditorProps {
  queryDescriptorId: string;
  placeholder?: string;
  onHelperInsert?: (helper: (value: string) => void) => void;
  onQueryChange?: (hasChanges: boolean) => void;
}

const CustomQueryEditor: React.FC<CustomQueryEditorProps> = ({
  queryDescriptorId,
  placeholder,
  onHelperInsert,
  onQueryChange,
}) => {
  const { t } = useTranslation('app');
  const [descriptor, setDescriptor] = useRecoilState(
    customQueryDescriptorById(queryDescriptorId),
  );
  const status = useRecoilValue(customQueryDataLoadStatus(queryDescriptorId));
  const setQueryState = useSetRecoilState(
    customQueryStateById(queryDescriptorId),
  );
  const [call, reset] = useLoadCustomQueryData();
  const [originalQuery, setOriginalQuery] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  const [queryError, setQueryError] = useRecoilState(
    customQueryErrors(queryDescriptorId),
  );

  const editorRef = useRef<any>(null);

  // Initialize original query when descriptor changes
  useEffect(() => {
    if (descriptor?.sql) {
      setOriginalQuery(descriptor.sql);
      setHasChanges(false);
    }
  }, [descriptor?.id]); // Only reset when the query ID changes

  // Check for changes when query is modified
  useEffect(() => {
    const currentQuery = descriptor?.sql ?? '';
    const hasQueryChanged = currentQuery !== originalQuery;
    setHasChanges(hasQueryChanged);
    onQueryChange?.(hasQueryChanged);
  }, [descriptor?.sql, originalQuery, onQueryChange]);

  function executeQuery() {
    if (_.isEmpty(descriptor?.sql?.trim())) {
      return;
    }
    call({
      queryId: queryDescriptorId,
    });
    setQueryState(true);
    // Update original query after execution
    setOriginalQuery(descriptor.sql);
    setHasChanges(false);
  }

  function setSqlQuery(sql: string) {
    setDescriptor(current => ({ ...current, sql }));
  }

  const sqlQuery = descriptor?.sql ?? '';
  const isLoading = status === AsyncLoadStatus.Loading;
  const isEmptyQuery = _.isEmpty(sqlQuery.trim());

  const formatSqlWithTemplates = (sql: string) => {
    // Temporarily replace all template variables with valid SQL identifiers
    // Format: {{...}} -> "TEMP_TEMPLATE_n"
    let templateMap = new Map();
    let templateCounter = 0;

    const tempSql = sql.replace(/{{[^}]+}}/g, match => {
      const placeholder = `"TEMP_TEMPLATE_${templateCounter}"`;
      templateMap.set(placeholder, match);
      templateCounter++;
      return placeholder;
    });

    // Format the SQL
    const formattedSql = format(tempSql, {
      language: 'trino',
    });

    // Restore all template variables
    return formattedSql.replace(/"TEMP_TEMPLATE_\d+"/g, placeholder =>
      templateMap.get(placeholder),
    );
  };

  const handleHelperInsert = useCallback((value: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      if (position) {
        editor.executeEdits('insert-helper', [
          {
            range: new editor.Range(
              position.lineNumber,
              position.column,
              position.lineNumber,
              position.column,
            ),
            text: value,
            forceMoveMarkers: true,
          },
        ]);
      }
    }
  }, []);

  // Expose the helper insert function to parent
  useEffect(() => {
    if (onHelperInsert) {
      onHelperInsert(handleHelperInsert);
    }
  }, [onHelperInsert, handleHelperInsert]);

  return (
    <>
      <Container
        data-component="CustomQueryEditor"
        col
        className="min-h-0 flex-1"
      >
        {!_.isEmpty(queryError) && (
          <PanelContainer
            id={`custom-query-error`}
            collapsible
            className="bg-alerts-error/70"
            title={t`Errors`}
          >
            <FormErrorField message={queryError} />
          </PanelContainer>
        )}
        <div className="h-full overflow-auto">
          <CodeEditor
            value={sqlQuery}
            onChange={query => setSqlQuery(query)}
            language={'trinosql'}
            placeholder={placeholder}
            className={'flex-1'}
          />
        </div>

        <ActionBar>
          {/* <ActionBar sticky stickyBottom> */}
          <CopyToClipboardButton
            className={cn('rounded-none')}
            hasIcon
            hasIconSmall
            value={sqlQuery}
          />
          {hasChanges && (
            <Button
              buttonSize="sm"
              label={t`Revert`}
              buttonType="secondary"
              onPress={() => setSqlQuery(originalQuery)}
            />
          )}
          <Spacer flexspace />
          <Button
            buttonSize="sm"
            label={t`Prettify`}
            buttonType="secondary"
            isLoading={isLoading}
            isDisabled={isLoading || isEmptyQuery}
            onPress={() => setSqlQuery(formatSqlWithTemplates(sqlQuery))}
          />
          <Button
            buttonSize="sm"
            label={isLoading ? t`Executing...` : t`Run`}
            buttonType="primary"
            isLoading={isLoading}
            hasIconBefore={isLoading}
            isDisabled={isLoading || isEmptyQuery}
            onPress={() => executeQuery()}
          />
        </ActionBar>
      </Container>
    </>
  );
};
export default CustomQueryEditor;
